<template>
  <div class="pg-root">
    <div class="pg-grain" aria-hidden="true"></div>

    <v-snackbar v-model="showSnackBar" :color="msgOk ? '#14532d' : '#7f1d1d'" top right :timeout="timeout">
      <div style="display:flex;align-items:center;gap:8px;">
        <v-icon small color="white">{{ msgOk ? "mdi-check-circle" : "mdi-alert-circle" }}</v-icon>
        <span style="font-weight:600;">{{ msg }}</span>
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn icon small v-bind="attrs" @click="showSnackBar = false">
          <v-icon small color="white">mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>

    <!-- ═══ LEFT: cinematic frame ═══ -->
    <section class="pg-stage d-none d-md-flex">
      <img src="../../assets/Images/ibimage12.jpg" alt="" class="pg-stage-img" />
      <div class="pg-stage-shade"></div>

      <div class="pg-vf" aria-hidden="true">
        <span class="pg-vf-c pg-vf-c--tl"></span>
        <span class="pg-vf-c pg-vf-c--tr"></span>
        <span class="pg-vf-c pg-vf-c--bl"></span>
        <span class="pg-vf-c pg-vf-c--br"></span>
        <span class="pg-vf-rec"><i></i> REC</span>
        <span class="pg-vf-tc">{{ timecode }}</span>
      </div>

      <div class="pg-stage-top">
        <router-link to="/" class="pg-brand">
          <span class="pg-brand-mark"><img src="../../assets/Images/ecoframe_logo.png" alt="ECOFRAME" /></span>
          <span class="pg-brand-text">
            <span class="pg-brand-name">ECOFRAME</span>
            <span class="pg-brand-sub">Kerala Forest Department</span>
          </span>
        </router-link>
        <router-link to="/login" class="pg-back">
          <v-icon small color="#e9f5ee">mdi-arrow-left</v-icon>
          Back to sign in
        </router-link>
      </div>

      <div class="pg-stage-copy">
        <div class="pg-eyebrow"><span></span>Account recovery</div>
        <h1 class="pg-headline">
          Lost your key?<br />
          <em>Back on set soon.</em>
        </h1>
        <p class="pg-lead">We send a one-time password to the mobile number registered with your account, then you choose a new password.</p>

        <div class="pg-note">
          <span class="pg-note-emblem"><img src="../../assets/Images/blacklogo.png" alt="" /></span>
          <span class="pg-note-text">
            <span class="pg-note-title">Kerala Forest Department</span>
            <span class="pg-note-sub">Government of Kerala · Secure official portal</span>
          </span>
          <v-icon color="#4ade80">mdi-shield-check-outline</v-icon>
        </div>
      </div>
    </section>

    <!-- ═══ RIGHT: reset ═══ -->
    <section class="pg-panel">
      <div class="pg-orb pg-orb--1" aria-hidden="true"></div>
      <div class="pg-orb pg-orb--2" aria-hidden="true"></div>
      <div class="pg-orb pg-orb--3" aria-hidden="true"></div>
      <div class="pg-dots" aria-hidden="true"></div>
      <div class="pg-panel-top">
        <router-link to="/" class="pg-brand d-flex d-md-none">
          <span class="pg-brand-mark"><img src="../../assets/Images/ecoframe_logo.png" alt="ECOFRAME" /></span>
          <span class="pg-brand-name">ECOFRAME</span>
        </router-link>
        <span class="pg-secure">
          <v-icon x-small color="#d6b97b">mdi-lock-reset</v-icon>
          Secure reset
        </span>
      </div>

      <div class="pg-panel-body">
        <div class="pg-card">
          <div class="pg-card-clap" aria-hidden="true"></div>

          <!-- Done -->
          <div v-if="done" class="pg-form pg-done">
            <span class="pg-done-icon"><v-icon size="34" color="#03140a">mdi-check-bold</v-icon></span>
            <div class="pg-card-head">
              <span class="pg-card-kicker">That's a wrap</span>
              <h2 class="pg-card-title">Password changed</h2>
              <p class="pg-card-sub">Sign in with your new password. Taking you there now…</p>
            </div>
            <router-link to="/login" class="pg-btn">
              Go to sign in
              <v-icon small color="#03140a">mdi-arrow-right</v-icon>
            </router-link>
          </div>

          <form v-else class="pg-form" @submit.prevent="submit">
            <div class="pg-card-head">
              <span class="pg-card-kicker">Scene 02 · Retake</span>
              <h2 class="pg-card-title">{{ step === 1 ? "Forgot password?" : "Set a new password" }}</h2>
              <p class="pg-card-sub">
                {{ step === 1
                  ? "Enter the mobile number registered with your account and we'll send you a one-time password (OTP)."
                  : "Enter the OTP we sent, then choose a new password." }}
              </p>
            </div>

            <ol class="pg-steps" aria-label="Steps">
              <li class="pg-step" :class="{ 'pg-step--on': step === 1, 'pg-step--done': step === 2 }">
                <span class="pg-step-dot">
                  <v-icon v-if="step === 2" x-small color="#03140a">mdi-check</v-icon>
                  <template v-else>1</template>
                </span>
                Mobile number
              </li>
              <li class="pg-step-line" aria-hidden="true"></li>
              <li class="pg-step" :class="{ 'pg-step--on': step === 2 }">
                <span class="pg-step-dot">2</span>
                New password
              </li>
            </ol>

            <!-- Step 1: mobile number -->
            <template v-if="step === 1">
              <label class="pg-field">
                <span class="pg-label">Mobile number</span>
                <span class="pg-box">
                  <v-icon small class="pg-box-icon">mdi-cellphone</v-icon>
                  <span class="pg-prefix">+91</span>
                  <input
                    v-model="mobNumber"
                    type="tel"
                    inputmode="numeric"
                    maxlength="10"
                    class="pg-input"
                    placeholder="10-digit mobile number"
                    autocomplete="tel-national"
                    @input="mobNumber = digits(mobNumber)"
                  />
                </span>
              </label>

              <div v-if="otpDisabled" class="pg-wait">
                <v-icon small color="#d6b97b">mdi-timer-sand</v-icon>
                Too many requests. Try again in {{ countdown }}.
              </div>

              <button type="submit" class="pg-btn" :disabled="loading || otpDisabled">
                <template v-if="!loading">
                  Send OTP
                  <v-icon small color="#03140a">mdi-arrow-right</v-icon>
                </template>
                <template v-else>
                  <v-icon small color="#03140a" class="pg-spin">mdi-loading</v-icon>
                  Sending…
                </template>
              </button>
            </template>

            <!-- Step 2: OTP and new password -->
            <template v-else>
              <div class="pg-sent">
                <v-icon small color="#4ade80">mdi-message-lock-outline</v-icon>
                <span>OTP sent to <b>{{ maskedNumber }}</b></span>
                <button type="button" class="pg-inline-link" @click="changeNumber">Change</button>
              </div>

              <label class="pg-field">
                <span class="pg-label-row">
                  <span class="pg-label">One-time password (OTP)</span>
                  <button type="button" class="pg-inline-link" :disabled="resendIn > 0 || loading || otpDisabled" @click="sendMobileNumber">
                    {{ resendIn > 0 ? "Resend in " + resendIn + "s" : "Resend OTP" }}
                  </button>
                </span>
                <span class="pg-box">
                  <v-icon small class="pg-box-icon">mdi-numeric</v-icon>
                  <input
                    v-model="otp"
                    type="text"
                    inputmode="numeric"
                    class="pg-input pg-input--otp"
                    placeholder="Enter the OTP"
                    autocomplete="one-time-code"
                    @input="otp = digits(otp)"
                  />
                </span>
              </label>

              <label class="pg-field">
                <span class="pg-label">New password</span>
                <span class="pg-box">
                  <v-icon small class="pg-box-icon">mdi-lock-outline</v-icon>
                  <input
                    v-model="password"
                    :type="show3 ? 'text' : 'password'"
                    class="pg-input"
                    placeholder="Choose a strong password"
                    autocomplete="new-password"
                  />
                  <button type="button" class="pg-eye" :aria-label="show3 ? 'Hide password' : 'Show password'" @click="show3 = !show3">
                    <v-icon small color="#9db3a6">{{ show3 ? "mdi-eye-outline" : "mdi-eye-off-outline" }}</v-icon>
                  </button>
                </span>
              </label>

              <ul class="pg-checklist" aria-label="Password requirements">
                <li v-for="r in passwordChecks" :key="r.label" :class="{ 'pg-check--ok': r.ok }">
                  <v-icon x-small>{{ r.ok ? "mdi-check-circle" : "mdi-circle-outline" }}</v-icon>
                  {{ r.label }}
                </li>
              </ul>

              <label class="pg-field">
                <span class="pg-label">Confirm new password</span>
                <span class="pg-box" :class="{ 'pg-box--bad': confirmPassword && !passwordsMatch }">
                  <v-icon small class="pg-box-icon">mdi-lock-check-outline</v-icon>
                  <input
                    v-model="confirmPassword"
                    :type="show4 ? 'text' : 'password'"
                    class="pg-input"
                    placeholder="Type it again"
                    autocomplete="new-password"
                  />
                  <button type="button" class="pg-eye" :aria-label="show4 ? 'Hide password' : 'Show password'" @click="show4 = !show4">
                    <v-icon small color="#9db3a6">{{ show4 ? "mdi-eye-outline" : "mdi-eye-off-outline" }}</v-icon>
                  </button>
                </span>
                <span v-if="confirmPassword" class="pg-match" :class="{ 'pg-match--ok': passwordsMatch }">
                  <v-icon x-small>{{ passwordsMatch ? "mdi-check-circle" : "mdi-alert-circle-outline" }}</v-icon>
                  {{ passwordsMatch ? "Passwords match" : "Passwords do not match" }}
                </span>
              </label>

              <button type="submit" class="pg-btn" :disabled="loading">
                <template v-if="!loading">
                  Reset password
                  <v-icon small color="#03140a">mdi-arrow-right</v-icon>
                </template>
                <template v-else>
                  <v-icon small color="#03140a" class="pg-spin">mdi-loading</v-icon>
                  Saving…
                </template>
              </button>
            </template>

            <div class="pg-divider"><span></span><em>Remembered it?</em><span></span></div>

            <router-link to="/login" class="pg-btn pg-btn--ghost">
              Back to sign in
            </router-link>
          </form>
        </div>

        <ul class="pg-perks">
          <li v-for="f in features" :key="f">
            <span class="pg-perk-mark"></span>
            {{ f }}
          </li>
        </ul>
      </div>

      <div class="pg-panel-foot">
        © {{ year }} Kerala Forest Department · Developed by <strong>Leopard Tech Labs Pvt. Ltd.</strong>
      </div>
    </section>
  </div>
</template>

<script>
import axios from "axios";
import CryptoJS from "crypto-js";

// Must match the keys /forgotpassword/verify-otp decrypts with
// (Backend/routes/userRoutes.js).
const OTP_KEY = 'film:"?>}|irgwwvfsn[!@#$%^&*()shooting';
const PASSWORD_KEY = "film*^@fs(&%eadirgwwvn[!@#$%^&*()shooting";
const USERID_KEY = 'film{}:">#)(&$#!rfswwvn[!@#$%^&*()shooting';

const PASSWORD_RULE = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;
const RESEND_AFTER = 30; // seconds before another OTP may be asked for
const RATE_LIMIT_WAIT = 120; // seconds to wait after the server's "too many requests"

// Password reset: mobile number -> OTP by SMS -> new password.
export default {
  data() {
    return {
      showSnackBar: false,
      timeout: 5000,
      msg: null,
      msgOk: false,
      step: 1,
      mobNumber: "",
      otp: "",
      password: "",
      confirmPassword: "",
      userId: "",
      show3: false,
      show4: false,
      loading: false,
      done: false,
      // Server rate limit (HTTP 429)
      otpDisabled: false,
      timer: 0,
      timerInterval: null,
      // Local wait before "Resend OTP"
      resendIn: 0,
      resendInterval: null,
      frame: 0,
      features: ["OTP to your registered mobile", "Strong password check", "Back to filming fast"],
    };
  },
  computed: {
    year() {
      return new Date().getFullYear();
    },
    // Monitor timecode HH:MM:SS:FF at 24 fps.
    timecode() {
      const f = this.frame;
      const pad = (n) => String(n).padStart(2, "0");
      const secs = Math.floor(f / 24);
      return [Math.floor(secs / 3600), Math.floor(secs / 60) % 60, secs % 60, f % 24].map(pad).join(":");
    },
    countdown() {
      return Math.floor(this.timer / 60) + ":" + String(this.timer % 60).padStart(2, "0");
    },
    maskedNumber() {
      const n = String(this.mobNumber || "");
      return n.length > 4 ? "•".repeat(n.length - 4) + n.slice(-4) : n;
    },
    passwordChecks() {
      const p = this.password || "";
      return [
        { label: "8+ characters", ok: p.length >= 8 },
        { label: "Uppercase letter", ok: /[A-Z]/.test(p) },
        { label: "Lowercase letter", ok: /[a-z]/.test(p) },
        { label: "Number", ok: /\d/.test(p) },
        { label: "Special character", ok: /[^A-Za-z0-9]/.test(p) },
      ];
    },
    passwordsMatch() {
      return !!this.password && this.password === this.confirmPassword;
    },
  },
  mounted() {
    const reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!reduce) this.frameTimer = setInterval(() => (this.frame += 1), 1000 / 24);
  },
  beforeDestroy() {
    clearInterval(this.frameTimer);
    clearInterval(this.timerInterval);
    clearInterval(this.resendInterval);
    clearTimeout(this.redirectTimer);
  },
  methods: {
    notify(text, ok = false) {
      this.msg = text;
      this.msgOk = ok;
      this.showSnackBar = true;
    },
    digits(value) {
      return String(value || "").replace(/\D/g, "");
    },
    submit() {
      if (this.loading) return;
      if (!this.mobNumber) {
        this.notify("Please enter your mobile number.");
        return;
      }
      if (!/^\d{10}$/.test(this.mobNumber)) {
        this.notify("Mobile number must be 10 digits.");
        return;
      }
      if (this.step === 1) {
        this.sendMobileNumber();
      } else if (this.validateInput()) {
        this.resetPassword();
      }
    },
    validateInput() {
      if (!this.otp) {
        this.notify("Please enter the OTP.");
        return false;
      }
      if (!this.password) {
        this.notify("Please enter a new password.");
        return false;
      }
      if (!PASSWORD_RULE.test(this.password)) {
        this.notify("Password must be at least 8 characters and include uppercase, lowercase, digit, and special character.");
        return false;
      }
      if (this.password !== this.confirmPassword) {
        this.notify("Passwords do not match.");
        return false;
      }
      return true;
    },
    changeNumber() {
      this.step = 1;
      this.otp = "";
      this.password = "";
      this.confirmPassword = "";
      this.userId = "";
    },
    sendMobileNumber() {
      if (this.loading) return;
      this.loading = true;
      axios({
        method: "POST",
        url: "/forgot/password",
        headers: { token: localStorage.getItem("token") },
        data: { mobNumber: this.mobNumber },
      })
        .then((response) => {
          this.loading = false;
          if (response.data.status) {
            this.notify(response.data.msg || "OTP sent.", true);
            this.userId = response.data.data.userId;
            this.step = 2;
            this.startResendWait();
          } else {
            this.notify(response.data.msg || "Failed to send OTP.");
          }
        })
        .catch((err) => {
          this.loading = false;
          if (err.response && err.response.status === 429) {
            this.notify(err.response.data.msg || "Too many requests. Please try again later.");
            this.otpDisabled = true;
            this.timer = RATE_LIMIT_WAIT;
            this.startTimer();
          } else {
            this.notify("Could not send the OTP. Please try again.");
            console.error(err);
          }
        });
    },
    resetPassword() {
      this.loading = true;
      axios({
        method: "POST",
        url: "/forgotpassword/verify-otp",
        data: {
          otp: CryptoJS.AES.encrypt(this.otp, OTP_KEY).toString(),
          password: CryptoJS.AES.encrypt(this.password, PASSWORD_KEY).toString(),
          userid: CryptoJS.AES.encrypt(String(this.userId), USERID_KEY).toString(),
        },
      })
        .then((response) => {
          this.loading = false;
          if (response.data.status) {
            this.notify(response.data.msg || "Password changed.", true);
            this.done = true;
            this.redirectTimer = setTimeout(() => this.$router.push({ path: "/login" }), 3000);
          } else {
            this.notify(response.data.msg || "Invalid or expired OTP.");
          }
        })
        .catch((error) => {
          this.loading = false;
          this.notify("An error occurred. Please try again.");
          console.error(error);
        });
    },
    startTimer() {
      clearInterval(this.timerInterval);
      this.timerInterval = setInterval(() => {
        if (this.timer > 0) {
          this.timer--;
        } else {
          this.otpDisabled = false;
          clearInterval(this.timerInterval);
        }
      }, 1000);
    },
    startResendWait() {
      clearInterval(this.resendInterval);
      this.resendIn = RESEND_AFTER;
      this.resendInterval = setInterval(() => {
        this.resendIn--;
        if (this.resendIn <= 0) clearInterval(this.resendInterval);
      }, 1000);
    },
  },
};
</script>

<style scoped>

/* Same look as LoginPage.vue / RegisterPage.vue */
.pg-root {
  --bg: #03100a;
  --line: rgba(233, 245, 238, 0.1);
  --line-strong: rgba(233, 245, 238, 0.18);
  --emerald: #22c55e;
  --emerald-soft: #4ade80;
  --gold: #d6b97b;
  --ink: #e9f5ee;
  --muted: #9db3a6;
  position: relative;
  min-height: 100vh;
  display: grid;
  grid-template-columns: minmax(0, 1.15fr) minmax(0, 1fr);
  font-family: "Inter", sans-serif;
  background: var(--bg);
  color: var(--ink);
  -webkit-font-smoothing: antialiased;
}
.pg-root a { text-decoration: none; }
@media (max-width: 959px) {
  .pg-root { grid-template-columns: 1fr; }
}

.pg-grain {
  position: fixed; inset: 0; z-index: 5; pointer-events: none; opacity: 0.07; mix-blend-mode: overlay;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>");
}

/* ── Brand ─────────────────────────────── */
.pg-brand { display: flex; align-items: center; gap: 12px; }
.pg-brand-mark { width: 42px; height: 42px; border-radius: 12px; background: #fff; display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0; box-shadow: 0 0 0 1px rgba(214, 185, 123, 0.45), 0 8px 20px rgba(0, 0, 0, 0.35); }
.pg-brand-mark img { width: 86%; height: 86%; object-fit: contain; }
.pg-brand-text { display: flex; flex-direction: column; line-height: 1.15; }
.pg-brand-name { font-size: 15px; font-weight: 700; letter-spacing: 4px; color: var(--ink); }
.pg-brand-sub { font-size: 10.5px; font-weight: 500; letter-spacing: 1.6px; text-transform: uppercase; color: rgba(233, 245, 238, 0.7); }

/* ── Stage ─────────────────────────────── */
.pg-stage {
  position: relative; overflow: hidden; min-height: 100vh;
  flex-direction: column; justify-content: space-between;
  padding: 44px clamp(44px, 5vw, 80px) clamp(56px, 8vh, 88px);
}
.pg-stage-img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; animation: pgDrift 30s ease-in-out infinite alternate; }
@keyframes pgDrift { from { transform: scale(1.05); } to { transform: scale(1.14) translate3d(-1.5%, -1%, 0); } }
.pg-stage-shade {
  position: absolute; inset: 0;
  background:
    linear-gradient(180deg, rgba(3, 16, 10, 0.6) 0%, rgba(3, 16, 10, 0.1) 30%, rgba(3, 16, 10, 0.35) 60%, rgba(3, 16, 10, 0.95) 100%),
    linear-gradient(90deg, rgba(3, 16, 10, 0.35), transparent 60%);
}
.pg-vf { position: absolute; inset: 22px; pointer-events: none; opacity: 0.8; }
.pg-vf-c { position: absolute; width: 26px; height: 26px; border: 0 solid rgba(233, 245, 238, 0.55); }
.pg-vf-c--tl { top: 0; left: 0; border-top-width: 1px; border-left-width: 1px; }
.pg-vf-c--tr { top: 0; right: 0; border-top-width: 1px; border-right-width: 1px; }
.pg-vf-c--bl { bottom: 0; left: 0; border-bottom-width: 1px; border-left-width: 1px; }
.pg-vf-c--br { bottom: 0; right: 0; border-bottom-width: 1px; border-right-width: 1px; }
.pg-vf-rec, .pg-vf-tc {
  position: absolute; bottom: 12px; display: inline-flex; align-items: center; gap: 7px;
  font-family: "Inter", sans-serif; font-size: 11.5px; letter-spacing: 1.5px; color: rgba(233, 245, 238, 0.75);
}
.pg-vf-rec { left: 40px; }
.pg-vf-tc { right: 40px; }
.pg-vf-rec i { width: 7px; height: 7px; border-radius: 50%; background: #ef4444; box-shadow: 0 0 10px #ef4444; animation: pgBlink 1.4s steps(2, start) infinite; }
@keyframes pgBlink { to { visibility: hidden; } }

.pg-stage-top { position: relative; z-index: 2; display: flex; align-items: center; justify-content: space-between; gap: 16px; }
.pg-back {
  display: inline-flex; align-items: center; gap: 6px; padding: 9px 16px; border-radius: 999px;
  background: rgba(255, 255, 255, 0.08); border: 1px solid var(--line-strong);
  backdrop-filter: blur(12px); -webkit-backdrop-filter: blur(12px);
  color: var(--ink); font-size: 13px; font-weight: 500; transition: background 0.2s;
}
.pg-back:hover { background: rgba(255, 255, 255, 0.16); }

.pg-stage-copy { position: relative; z-index: 2; max-width: 560px; }
.pg-stage-copy > * { animation: pgRise 1s cubic-bezier(0.2, 0.7, 0.2, 1) both; }
.pg-stage-copy > *:nth-child(2) { animation-delay: 0.1s; }
.pg-stage-copy > *:nth-child(3) { animation-delay: 0.2s; }
.pg-stage-copy > *:nth-child(4) { animation-delay: 0.3s; }
@keyframes pgRise { from { opacity: 0; transform: translateY(22px); } to { opacity: 1; transform: none; } }
.pg-eyebrow { display: flex; align-items: center; gap: 14px; margin-bottom: 22px; font-size: 11.5px; font-weight: 600; letter-spacing: 2.6px; text-transform: uppercase; color: var(--gold); }
.pg-eyebrow span { width: 40px; height: 1px; background: linear-gradient(90deg, var(--gold), transparent); }
.pg-headline { font-family: "Inter", sans-serif; font-weight: 500; font-size: clamp(42px, 4.6vw, 70px); line-height: 1.04; letter-spacing: -1px; margin: 0 0 18px; color: #f4faf6; }
.pg-headline em { font-style: italic; color: var(--emerald-soft); }
.pg-lead { max-width: 470px; margin: 0 0 32px; font-size: 16.5px; font-weight: 300; line-height: 1.7; color: rgba(233, 245, 238, 0.82); }
.pg-note {
  display: flex; align-items: center; gap: 14px; max-width: 440px; padding: 14px 18px; border-radius: 18px;
  background: rgba(255, 255, 255, 0.08); border: 1px solid var(--line-strong);
  backdrop-filter: blur(18px); -webkit-backdrop-filter: blur(18px);
}
.pg-note-emblem { width: 42px; height: 42px; border-radius: 50%; background: #fff; display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0; }
.pg-note-emblem img { width: 76%; height: 76%; object-fit: contain; }
.pg-note-text { display: flex; flex-direction: column; flex: 1; line-height: 1.35; }
.pg-note-title { font-size: 14px; font-weight: 600; }
.pg-note-sub { font-size: 12px; color: rgba(233, 245, 238, 0.7); }

/* ── Panel ─────────────────────────────── */
.pg-panel { position: relative; min-height: 100vh; display: flex; flex-direction: column; background: var(--bg); overflow: hidden; }

.pg-orb { position: absolute; border-radius: 50%; pointer-events: none; filter: blur(80px); }
.pg-orb--1 { width: 420px; height: 420px; top: -100px; right: -100px; background: radial-gradient(circle, rgba(34,197,94,0.22) 0%, transparent 70%); animation: pgOrbDrift1 14s ease-in-out infinite alternate; }
.pg-orb--2 { width: 320px; height: 320px; bottom: 60px; left: -80px; background: radial-gradient(circle, rgba(214,185,123,0.18) 0%, transparent 70%); animation: pgOrbDrift2 18s ease-in-out infinite alternate; }
.pg-orb--3 { width: 240px; height: 240px; top: 45%; right: 10%; background: radial-gradient(circle, rgba(74,222,128,0.12) 0%, transparent 70%); animation: pgOrbDrift3 11s ease-in-out infinite alternate; }
@keyframes pgOrbDrift1 { from { transform: translate(0,0) scale(1); } to { transform: translate(-30px, 40px) scale(1.1); } }
@keyframes pgOrbDrift2 { from { transform: translate(0,0) scale(1); } to { transform: translate(20px,-30px) scale(0.9); } }
@keyframes pgOrbDrift3 { from { transform: translate(0,0); } to { transform: translate(-20px,20px); } }

.pg-dots { position: absolute; inset: 0; pointer-events: none; opacity: 0.18; background-image: radial-gradient(circle, rgba(233,245,238,0.55) 1px, transparent 1px); background-size: 28px 28px; }
.pg-panel-top { position: relative; z-index: 1; display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 30px clamp(20px, 4vw, 56px) 0; }
.pg-secure { margin-left: auto; display: inline-flex; align-items: center; gap: 6px; font-size: 11.5px; font-weight: 600; letter-spacing: 1.6px; text-transform: uppercase; color: var(--muted); }
.pg-panel-body { position: relative; z-index: 1; flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 32px clamp(20px, 4vw, 56px); }

.pg-card {
  position: relative; width: 100%; max-width: 440px; border-radius: 26px; overflow: hidden;
  background: linear-gradient(165deg, rgba(255, 255, 255, 0.09) 0%, rgba(255, 255, 255, 0.03) 100%);
  border: 1px solid var(--line-strong);
  box-shadow: 0 40px 90px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(74,222,128,0.06) inset, inset 0 1px 0 rgba(255, 255, 255, 0.12);
  animation: pgRise 0.9s cubic-bezier(0.2, 0.7, 0.2, 1) both;
  backdrop-filter: blur(20px); -webkit-backdrop-filter: blur(20px);
}
.pg-card::before { content: ""; position: absolute; inset: 0; border-radius: 26px; padding: 1px; background: linear-gradient(150deg, rgba(214, 185, 123, 0.7), transparent 35%, transparent 70%, rgba(74, 222, 128, 0.5)); -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0); -webkit-mask-composite: xor; mask-composite: exclude; pointer-events: none; animation: pgShimmer 5s ease-in-out infinite alternate; }
@keyframes pgShimmer { from { opacity: 0.7; } to { opacity: 1; } }
.pg-card::after { content: ""; position: absolute; top: 0; left: -60%; width: 40%; height: 100%; background: linear-gradient(90deg, transparent, rgba(255,255,255,0.04), transparent); transform: skewX(-15deg); animation: pgSweep 8s ease-in-out 2s infinite; pointer-events: none; border-radius: 26px; }
@keyframes pgSweep { 0%,100% { left: -60%; opacity: 0; } 10% { opacity: 1; } 40% { left: 140%; opacity: 0; } }
.pg-card-clap { height: 7px; background: repeating-linear-gradient(115deg, rgba(233, 245, 238, 0.9) 0 12px, rgba(74,222,128,0.4) 12px 14px, transparent 14px 26px); opacity: 0.7; position: relative; }
.pg-card-clap::after { content: ""; position: absolute; inset: 0; background: linear-gradient(90deg, transparent, rgba(74,222,128,0.3), transparent); animation: pgClapScan 3s ease-in-out infinite; }
@keyframes pgClapScan { 0%,100% { opacity: 0; transform: translateX(-100%); } 50% { opacity: 1; transform: translateX(100%); } }

.pg-form { display: flex; flex-direction: column; gap: 18px; padding: 34px clamp(22px, 4vw, 38px) 36px; }
.pg-card-head { margin-bottom: 2px; }
.pg-card-kicker { display: block; margin-bottom: 12px; font-size: 11px; font-weight: 600; letter-spacing: 2.6px; text-transform: uppercase; color: var(--gold); }
.pg-card-title { font-family: "Inter", sans-serif; font-weight: 500; font-size: 36px; line-height: 1.1; letter-spacing: -0.5px; margin: 0 0 8px; color: #f4faf6; }
.pg-card-sub { margin: 0; font-size: 14px; font-weight: 300; line-height: 1.6; color: var(--muted); }

/* Steps */
.pg-steps { display: flex; align-items: center; gap: 10px; margin: 0; padding: 0; list-style: none; }
.pg-step { display: inline-flex; align-items: center; gap: 8px; font-size: 12.5px; font-weight: 500; color: var(--muted); white-space: nowrap; }
.pg-step-dot { display: inline-flex; align-items: center; justify-content: center; width: 24px; height: 24px; border-radius: 50%; border: 1px solid var(--line-strong); font-size: 12px; font-weight: 700; color: var(--muted); transition: background 0.3s ease, border-color 0.3s ease, color 0.3s ease; }
.pg-step--on { color: var(--ink); }
.pg-step--on .pg-step-dot { border-color: var(--emerald-soft); color: var(--emerald-soft); box-shadow: 0 0 0 4px rgba(74, 222, 128, 0.12); }
.pg-step--done .pg-step-dot { background: var(--emerald-soft); border-color: var(--emerald-soft); }
.pg-step-line { flex: 1; height: 1px; background: linear-gradient(90deg, rgba(74, 222, 128, 0.5), var(--line)); }

.pg-field { display: flex; flex-direction: column; gap: 9px; }
.pg-label-row { display: flex; align-items: baseline; justify-content: space-between; gap: 12px; }
.pg-label { font-size: 12.5px; font-weight: 500; letter-spacing: 0.3px; color: rgba(233, 245, 238, 0.85); }
.pg-box {
  display: flex; align-items: center; gap: 10px; padding: 0 16px;
  background: rgba(1, 8, 5, 0.6); border: 1px solid var(--line-strong); border-radius: 14px;
  transition: border-color 0.3s ease, box-shadow 0.3s ease, background 0.3s ease;
}
.pg-box:hover { border-color: rgba(233, 245, 238, 0.3); background: rgba(1, 8, 5, 0.7); }
.pg-box:focus-within { border-color: var(--emerald-soft); background: rgba(1, 8, 5, 0.8); box-shadow: 0 0 0 4px rgba(74, 222, 128, 0.15), 0 0 20px rgba(74,222,128,0.08); }
.pg-box--bad, .pg-box--bad:focus-within { border-color: rgba(248, 113, 113, 0.7); box-shadow: 0 0 0 4px rgba(248, 113, 113, 0.12); }
.pg-box-icon { color: var(--muted) !important; transition: color 0.3s ease; }
.pg-box:focus-within .pg-box-icon { color: var(--emerald-soft) !important; }
.pg-prefix { padding-right: 10px; border-right: 1px solid var(--line-strong); font-size: 14px; font-weight: 600; color: var(--muted); }
.pg-input { flex: 1; min-width: 0; padding: 15px 0; border: none; outline: none; background: transparent; color: var(--ink); font-family: inherit; font-size: 15px; }
.pg-input--otp { font-family: "Inter", sans-serif; font-size: 17px; letter-spacing: 6px; }
.pg-input::placeholder { color: rgba(157, 179, 166, 0.45); font-family: "Inter", sans-serif; font-size: 15px; letter-spacing: 0; }
.pg-input:-webkit-autofill { -webkit-text-fill-color: var(--ink); -webkit-box-shadow: 0 0 0 40px #06150d inset; caret-color: var(--ink); }
.pg-eye { display: flex; padding: 4px; border: none; background: none; cursor: pointer; }

.pg-inline-link { padding: 0; border: none; background: none; cursor: pointer; font-family: inherit; font-size: 12.5px; font-weight: 600; color: var(--emerald-soft); }
.pg-inline-link:hover:not(:disabled) { text-decoration: underline; }
.pg-inline-link:disabled { cursor: default; color: var(--muted); }

.pg-sent { display: flex; align-items: center; gap: 8px; padding: 11px 14px; border-radius: 12px; background: rgba(74, 222, 128, 0.08); border: 1px solid rgba(74, 222, 128, 0.22); font-size: 13px; color: var(--ink); }
.pg-sent b { font-family: "Inter", sans-serif; font-weight: 500; letter-spacing: 1px; }
.pg-sent .pg-inline-link { margin-left: auto; }

.pg-wait { display: flex; align-items: center; gap: 8px; padding: 10px 14px; border-radius: 12px; background: rgba(214, 185, 123, 0.1); border: 1px solid rgba(214, 185, 123, 0.3); font-size: 13px; color: var(--gold); }

.pg-checklist { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 6px 14px; margin: -6px 0 0; padding: 0; list-style: none; }
.pg-checklist li { display: inline-flex; align-items: center; gap: 6px; font-size: 12px; color: var(--muted); transition: color 0.2s ease; }
.pg-checklist li .v-icon { color: rgba(157, 179, 166, 0.6) !important; }
.pg-checklist li.pg-check--ok { color: var(--ink); }
.pg-checklist li.pg-check--ok .v-icon { color: var(--emerald-soft) !important; }

.pg-match { display: inline-flex; align-items: center; gap: 6px; font-size: 12px; color: #fca5a5; }
.pg-match .v-icon { color: inherit !important; }
.pg-match--ok { color: var(--emerald-soft); }

.pg-btn {
  display: flex; align-items: center; justify-content: center; gap: 10px; width: 100%;
  margin-top: 4px; padding: 16px; border: none; border-radius: 999px; cursor: pointer;
  background: linear-gradient(135deg, #4ade80 0%, #22c55e 50%, #16a34a 100%);
  color: #03140a; font-family: inherit; font-size: 15.5px; font-weight: 700; letter-spacing: 0.3px;
  box-shadow: 0 1px 0 rgba(255, 255, 255, 0.5) inset, 0 14px 34px rgba(34, 197, 94, 0.38), 0 0 0 0 rgba(74, 222, 128, 0);
  transition: transform 0.25s ease, box-shadow 0.25s ease;
  animation: pgBtnPulse 3s ease-in-out infinite;
  position: relative; overflow: hidden;
}
.pg-btn::after { content: ""; position: absolute; inset: 0; border-radius: 999px; background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent); transform: translateX(-100%); transition: transform 0.6s ease; }
.pg-btn:hover::after { transform: translateX(100%); }
@keyframes pgBtnPulse { 0%,100% { box-shadow: 0 1px 0 rgba(255,255,255,0.5) inset, 0 14px 34px rgba(34,197,94,0.38), 0 0 0 0 rgba(74,222,128,0); } 50% { box-shadow: 0 1px 0 rgba(255,255,255,0.5) inset, 0 14px 34px rgba(34,197,94,0.38), 0 0 20px 4px rgba(74,222,128,0.18); } }
.pg-btn:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 1px 0 rgba(255, 255, 255, 0.5) inset, 0 22px 50px rgba(34, 197, 94, 0.5), 0 0 30px rgba(74,222,128,0.25); animation: none; }
.pg-btn:disabled { opacity: 0.6; cursor: not-allowed; animation: none; }
.pg-btn .v-icon { transition: transform 0.25s ease; }
.pg-btn:hover .v-icon { transform: translateX(4px); }
.pg-btn--ghost { margin-top: 0; background: transparent; color: var(--ink); border: 1px solid var(--line-strong); box-shadow: none; font-size: 14.5px; font-weight: 500; animation: none; }
.pg-btn--ghost::after { display: none; }
.pg-btn--ghost:hover:not(:disabled) { background: rgba(255, 255, 255, 0.06); border-color: rgba(214, 185, 123, 0.7); box-shadow: 0 0 20px rgba(214,185,123,0.1); }
.pg-spin { animation: pgSpin 0.8s linear infinite; }
@keyframes pgSpin { to { transform: rotate(360deg); } }

.pg-divider { display: flex; align-items: center; gap: 14px; }
.pg-divider span { flex: 1; height: 1px; background: var(--line); }
.pg-divider em { font-style: normal; font-size: 12px; color: var(--muted); white-space: nowrap; }

/* Done */
.pg-done { align-items: center; text-align: center; }
.pg-done-icon { display: flex; align-items: center; justify-content: center; width: 72px; height: 72px; border-radius: 50%; background: linear-gradient(135deg, #4ade80 0%, #16a34a 100%); box-shadow: 0 0 0 8px rgba(74, 222, 128, 0.12), 0 18px 40px rgba(34, 197, 94, 0.35); animation: pgPop 0.6s cubic-bezier(0.2, 1.4, 0.4, 1) both; }
@keyframes pgPop { from { transform: scale(0.4); opacity: 0; } to { transform: none; opacity: 1; } }

.pg-perks { list-style: none; display: flex; flex-wrap: wrap; justify-content: center; gap: 10px 26px; margin: 28px 0 0; padding: 0; }
.pg-perks li { display: inline-flex; align-items: center; gap: 9px; font-size: 13px; color: var(--muted); }
.pg-perk-mark { width: 6px; height: 6px; transform: rotate(45deg); background: var(--gold); }

.pg-panel-foot { position: relative; z-index: 1; padding: 0 20px 26px; text-align: center; font-size: 12px; color: rgba(157, 179, 166, 0.65); }
.pg-panel-foot strong { font-weight: 500; color: var(--muted); }

@media (max-width: 480px) {
  .pg-secure { display: none; }
  .pg-card-title { font-size: 30px; }
  .pg-form { padding: 28px 22px 30px; }
  .pg-step { font-size: 11.5px; }
  .pg-checklist { grid-template-columns: 1fr; }
}
@media (prefers-reduced-motion: reduce) {
  .pg-stage-img, .pg-stage-copy > *, .pg-card, .pg-vf-rec i, .pg-btn, .pg-orb, .pg-card::before, .pg-card::after, .pg-card-clap::after, .pg-done-icon { animation: none; }
}
</style>
